/**
 * PolygonEditor — SVG 拖點編輯器、沿用 iOS PolygonEditorScene UX：
 * - 拖角點：移位
 * - 點邊：插入新角點
 * - 雙擊角點：刪除
 * - 工具列：矩形 4×3、圓形 16 段重設
 */

import { FreePolygon, Point2D } from '../lib/polygon';

export interface EditorOptions {
  container: HTMLElement;
  initial: FreePolygon;
  onChange?: (p: FreePolygon) => void;
}

const PADDING = 24;          // px
const POINT_RADIUS = 10;     // px (手指好點)
const SNAP_GRID_M = 0.05;    // 5 cm 網格
const ORTHO_THRESHOLD_M = 0.10;  // 10 cm 內視為「想對齊」

export class PolygonEditor {
  private svg: SVGSVGElement;
  private polygon: FreePolygon;
  private dragIndex: number | null = null;
  private viewW = 600;
  private viewH = 400;
  private metersPerPx = 0.02;  // 初始 1 px = 2 cm、會 fit-to-bbox 重算
  /** 直角模式（預設 ON）— 拖點時鄰邊強制水平 / 垂直 */
  orthoMode = true;
  /** 網格 snap（預設 ON）— 拖完吸到 5cm 整數 */
  gridSnap = true;

  constructor(private opts: EditorOptions) {
    this.polygon = opts.initial;
    this.svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    this.svg.setAttribute('width', '100%');
    this.svg.setAttribute('height', '100%');
    this.svg.style.touchAction = 'none';
    this.svg.style.background = '#fafafa';
    this.svg.style.border = '1px solid #ddd';
    this.svg.style.borderRadius = '8px';
    opts.container.appendChild(this.svg);
    this.bind();
    this.render();
  }

  reset(p: FreePolygon) {
    this.polygon = p;
    this.render();
    this.opts.onChange?.(p);
  }

  getPolygon() { return this.polygon; }

  private bind() {
    this.svg.addEventListener('pointerdown', (e) => this.onDown(e));
    this.svg.addEventListener('pointermove', (e) => this.onMove(e));
    this.svg.addEventListener('pointerup', (e) => this.onUp(e));
    this.svg.addEventListener('pointercancel', (e) => this.onUp(e));
    this.svg.addEventListener('dblclick', (e) => this.onDoubleClick(e));
  }

  private fitToBBox() {
    const rect = this.svg.getBoundingClientRect();
    this.viewW = rect.width || 600;
    this.viewH = rect.height || 400;
    const bb = this.polygon.boundingBox;
    const scaleX = (this.viewW - PADDING * 2) / Math.max(0.1, bb.width);
    const scaleY = (this.viewH - PADDING * 2) / Math.max(0.1, bb.height);
    this.metersPerPx = 1 / Math.min(scaleX, scaleY);
  }

  private worldToScreen(p: Point2D): { x: number; y: number } {
    const bb = this.polygon.boundingBox;
    const pxPerM = 1 / this.metersPerPx;
    const cx = this.viewW / 2;
    const cy = this.viewH / 2;
    const bbcx = bb.x + bb.width / 2;
    const bbcy = bb.y + bb.height / 2;
    return {
      x: cx + (p.x - bbcx) * pxPerM,
      y: cy + (p.y - bbcy) * pxPerM,
    };
  }

  private screenToWorld(sx: number, sy: number): Point2D {
    const bb = this.polygon.boundingBox;
    const pxPerM = 1 / this.metersPerPx;
    const cx = this.viewW / 2;
    const cy = this.viewH / 2;
    const bbcx = bb.x + bb.width / 2;
    const bbcy = bb.y + bb.height / 2;
    return {
      x: (sx - cx) / pxPerM + bbcx,
      y: (sy - cy) / pxPerM + bbcy,
    };
  }

  private hitTestVertex(sx: number, sy: number): number | null {
    for (let i = 0; i < this.polygon.vertices.length; i++) {
      const p = this.worldToScreen(this.polygon.vertices[i]);
      if (Math.hypot(p.x - sx, p.y - sy) < POINT_RADIUS * 1.6) return i;
    }
    return null;
  }

  private hitTestEdge(sx: number, sy: number): { i: number; t: number } | null {
    const v = this.polygon.vertices;
    for (let i = 0; i < v.length; i++) {
      const a = this.worldToScreen(v[i]);
      const b = this.worldToScreen(v[(i + 1) % v.length]);
      const dx = b.x - a.x, dy = b.y - a.y;
      const len2 = dx * dx + dy * dy;
      if (len2 < 1) continue;
      const t = ((sx - a.x) * dx + (sy - a.y) * dy) / len2;
      if (t < 0.05 || t > 0.95) continue;
      const px = a.x + dx * t, py = a.y + dy * t;
      if (Math.hypot(px - sx, py - sy) < POINT_RADIUS) return { i, t };
    }
    return null;
  }

  private onDown(e: PointerEvent) {
    const r = this.svg.getBoundingClientRect();
    const sx = e.clientX - r.left, sy = e.clientY - r.top;
    const vi = this.hitTestVertex(sx, sy);
    if (vi !== null) {
      this.dragIndex = vi;
      this.svg.setPointerCapture(e.pointerId);
      return;
    }
    const edge = this.hitTestEdge(sx, sy);
    if (edge) {
      const w = this.screenToWorld(sx, sy);
      this.polygon.vertices.splice(edge.i + 1, 0, w);
      this.render();
      this.opts.onChange?.(this.polygon);
    }
  }

  private onMove(e: PointerEvent) {
    if (this.dragIndex === null) return;
    const r = this.svg.getBoundingClientRect();
    const sx = e.clientX - r.left, sy = e.clientY - r.top;
    let w = this.screenToWorld(sx, sy);

    // 直角模式：拖點時、強制鄰兩邊保持水平 / 垂直
    if (this.orthoMode) {
      w = this.applyOrthoConstraint(this.dragIndex, w);
    }

    this.polygon.vertices[this.dragIndex] = w;
    this.render();
    this.opts.onChange?.(this.polygon);
  }

  private onUp(e: PointerEvent) {
    if (this.dragIndex !== null) {
      // 放開時 snap 到 5cm 整數
      if (this.gridSnap) {
        const v = this.polygon.vertices[this.dragIndex];
        v.x = Math.round(v.x / SNAP_GRID_M) * SNAP_GRID_M;
        v.y = Math.round(v.y / SNAP_GRID_M) * SNAP_GRID_M;
        // snap 後再跑一次 ortho、保證直角
        if (this.orthoMode) {
          this.polygon.vertices[this.dragIndex] =
            this.applyOrthoConstraint(this.dragIndex, v);
        }
        this.render();
        this.opts.onChange?.(this.polygon);
      }
      this.svg.releasePointerCapture(e.pointerId);
      this.dragIndex = null;
    }
  }

  /**
   * 直角模式：被拖點 P 的前後鄰點 prev / next、強制邊 prev-P / P-next 水平 or 垂直。
   * 策略：取被拖點原向量 dx/dy、選變化較大的軸跟隨、另一軸吸到鄰點。
   * 兩鄰邊各自決定方向、若衝突優先「前一邊」(prev → P) 對齊。
   */
  private applyOrthoConstraint(i: number, p: Point2D): Point2D {
    const n = this.polygon.vertices.length;
    const prev = this.polygon.vertices[(i - 1 + n) % n];
    const next = this.polygon.vertices[(i + 1) % n];
    let { x, y } = p;
    // prev → P 邊：若 dx 比 dy 小、表示這邊應該垂直 (x 對齊 prev.x)
    const dxPrev = Math.abs(x - prev.x);
    const dyPrev = Math.abs(y - prev.y);
    if (dxPrev < dyPrev) x = prev.x;   // 垂直邊
    else y = prev.y;                    // 水平邊
    // P → next 邊：同樣 logic、補另一個自由度
    const dxNext = Math.abs(x - next.x);
    const dyNext = Math.abs(y - next.y);
    if (dxNext < dyNext) {
      // 想垂直、但 x 已被 prev 鎖、若 x ≠ next.x 維持
      // 若這邊也要垂直、改 y = next.y 用次要對齊
      if (Math.abs(x - next.x) > ORTHO_THRESHOLD_M) y = next.y;
    } else {
      if (Math.abs(y - next.y) > ORTHO_THRESHOLD_M) x = next.x;
    }
    return { x, y };
  }

  private onDoubleClick(e: MouseEvent) {
    const r = this.svg.getBoundingClientRect();
    const vi = this.hitTestVertex(e.clientX - r.left, e.clientY - r.top);
    if (vi !== null && this.polygon.vertices.length > 3) {
      this.polygon.vertices.splice(vi, 1);
      this.render();
      this.opts.onChange?.(this.polygon);
    }
  }

  private render() {
    this.fitToBBox();
    const v = this.polygon.vertices;
    while (this.svg.firstChild) this.svg.removeChild(this.svg.firstChild);

    // 多邊形填色
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
    const pts = v.map(p => {
      const s = this.worldToScreen(p);
      return `${s.x},${s.y}`;
    }).join(' ');
    path.setAttribute('points', pts);
    path.setAttribute('fill', 'rgba(99,102,241,0.15)');
    path.setAttribute('stroke', 'rgb(99,102,241)');
    path.setAttribute('stroke-width', '2');
    this.svg.appendChild(path);

    // 邊長文字
    for (let i = 0; i < v.length; i++) {
      const a = this.worldToScreen(v[i]);
      const b = this.worldToScreen(v[(i + 1) % v.length]);
      const lenM = Math.hypot(v[(i + 1) % v.length].x - v[i].x,
                              v[(i + 1) % v.length].y - v[i].y);
      const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      text.setAttribute('x', String(mx));
      text.setAttribute('y', String(my - 6));
      text.setAttribute('text-anchor', 'middle');
      text.setAttribute('font-size', '12');
      text.setAttribute('fill', '#374151');
      text.textContent = `${lenM.toFixed(2)} m`;
      this.svg.appendChild(text);
    }

    // 角點
    for (let i = 0; i < v.length; i++) {
      const s = this.worldToScreen(v[i]);
      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.setAttribute('cx', String(s.x));
      circle.setAttribute('cy', String(s.y));
      circle.setAttribute('r', String(POINT_RADIUS));
      circle.setAttribute('fill', '#fff');
      circle.setAttribute('stroke', 'rgb(99,102,241)');
      circle.setAttribute('stroke-width', '2');
      this.svg.appendChild(circle);
      const num = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      num.setAttribute('x', String(s.x));
      num.setAttribute('y', String(s.y + 4));
      num.setAttribute('text-anchor', 'middle');
      num.setAttribute('font-size', '10');
      num.setAttribute('fill', 'rgb(99,102,241)');
      num.textContent = String(i + 1);
      this.svg.appendChild(num);
    }
  }
}

export function approximateCircle(diameterM: number, segments = 16): FreePolygon {
  const r = diameterM / 2;
  const pts: Point2D[] = [];
  for (let i = 0; i < segments; i++) {
    const a = (i / segments) * Math.PI * 2;
    pts.push({ x: r + Math.cos(a) * r, y: r + Math.sin(a) * r });
  }
  return new FreePolygon(pts);
}
